import { useContext, useEffect, useState } from 'react';
import { Modal, ModalDialog, ModalOverflow, Stack, Typography, FormControl, FormLabel, Input, Button, Select, Option, Chip, Autocomplete, Box, IconButton, Sheet, ModalClose } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutContext } from '../../context';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import { APIGetPopularTags } from '../../api';



export default function FilterModal({ open, onClose, onApplyFilters, initialFilters = {} }) {
    const { deviceType, token } = useContext(LayoutContext);
    
    const [filters, setFilters] = useState({
        city: '',
        memberCount: '',
        type: 'all',
        tags: [],
        searchText: ''
    });

    const [availableTags, setAvailableTags] = useState([]);
    const [loadingTags, setLoadingTags] = useState(false);

    // Загрузка популярных тегов при открытии
    useEffect(() => {
        const loadPopularTags = async () => {
            if (open && token) {
                setLoadingTags(true);
                try {
                    const tagsData = await APIGetPopularTags(token);
                    const tags = tagsData.popular_tags?.map(item => item.tag) || [];
                    setAvailableTags(tags);
                } catch (error) {
                    console.error('Ошибка загрузки тегов:', error);
                    // Запасные теги на случай ошибки
                    setAvailableTags(['путешествия', 'спорт', 'искусство', 'бизнес', 'технологии']);
                } finally {
                    setLoadingTags(false);
                }
            }
        };
        
        loadPopularTags();
    }, [open, token]);

    // Инициализация фильтров
    useEffect(() => {
        if (open) {
            setFilters(initialFilters);
        }
    }, [open, initialFilters]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleApply = () => {
        // Очищаем пустые значения
        const cleanedFilters = Object.fromEntries(
            Object.entries(filters).filter(([_, value]) => {
                if (Array.isArray(value)) return value.length > 0;
                if (typeof value === 'string') return value.trim() !== '';
                return value !== '';
            })
        );
        
        onApplyFilters(cleanedFilters);
        onClose();
    };

    const handleReset = () => {
        setFilters({
            city: '',
            memberCount: '',
            type: 'all',
            tags: [],
            searchText: ''
        });
    };

    const hasActiveFilters = Object.keys(filters).some(key => {
        const value = filters[key];
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'string') return value.trim() !== '';
        return value !== '';
    });

    return (
        <AnimatePresence>
            {open && (
                <Modal
                    sx={{ zIndex: 10001 }}
                    open={open}
                    onClose={onClose}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3, ease: "easeInOut" },
                        }
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto' }}>
                        <ModalDialog
                            layout="fullscreen"
                            size="lg"
                            component={motion.div}
                            initial={{ opacity: 0, y: -10, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.98 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            sx={(theme) => ({
                                [theme.breakpoints.up("xl")]: { width: '500px', mx: 0 },
                                [theme.breakpoints.down("xl")]: { width: '500px', mx: 0 },
                                [theme.breakpoints.down("lg")]: { width: '500px', mx: 0 },
                                [theme.breakpoints.down("md")]: { width: '400px', mx: 0 },
                                [theme.breakpoints.down("sm")]: { width: '95%', p: 3 },
                                width: '100%'
                            })}
                        >
                            <Box sx={{ mt: deviceType == 'Phone' ? 10 : 0 }}></Box>
                            <ModalClose sx={{ mt: deviceType == 'Phone' ? 10 : 0 }} onClick={onClose}/>
                            
                            <Stack direction={"column"} alignItems={'center'} spacing={2} sx={{ mt: 2, mb: 1 }}>
                                <FilterListRoundedIcon sx={{ fontSize: 40, color: 'primary.500' }} />
                                <Typography level="h3">Фильтры чатов</Typography>
                            </Stack>
                            
                            <Stack spacing={3} sx={{ width: '100%', maxWidth: 400, mx: 'auto', mt: 3 }}>
                                {/* Тип чата */}
                                <FormControl>
                                    <FormLabel>Тип чата</FormLabel>
                                    <Select
                                        slotProps={{ listbox: { sx: { zIndex: 10010 } } }}
                                        value={filters.type}
                                        onChange={(e, value) => handleFilterChange('type', value)}
                                    >
                                        <Option value="all">Все чаты</Option>
                                        <Option value="community">Сообщества</Option>
                                        <Option value="personal">Личные</Option>
                                    </Select>
                                </FormControl>

                                {/* Город */}
                                <FormControl>
                                    <FormLabel>Город</FormLabel>
                                    <Input
                                        value={filters.city}
                                        onChange={(e) => handleFilterChange('city', e.target.value)}
                                        placeholder="Любой город"
                                    />
                                </FormControl>

                                {/* Количество участников */}
                                <FormControl>
                                    <FormLabel>Участников</FormLabel>
                                    <Select
                                        slotProps={{ listbox: { sx: { zIndex: 10010 } } }}
                                        value={filters.memberCount}
                                        onChange={(e, value) => handleFilterChange('memberCount', value)}
                                    >
                                        <Option value="">Любое количество</Option>
                                        <Option value="small">До 10 человек</Option>
                                        <Option value="medium">10-50 человек</Option>
                                        <Option value="large">Более 50 человек</Option>
                                    </Select>
                                </FormControl>

                                {/* Теги */}
                                <FormControl>
                                    <FormLabel>Теги сообществ</FormLabel>
                                    <Autocomplete
                                        multiple
                                        options={availableTags}
                                        value={filters.tags}
                                        onChange={(event, newValue) => handleFilterChange('tags', newValue)}
                                        placeholder="Выберите теги"
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    variant="soft"
                                                    color="primary"
                                                    {...getTagProps({ index })}
                                                >
                                                    #{option}
                                                </Chip>
                                            ))
                                        }
                                    />
                                </FormControl>

                                {/* Поисковый запрос */}
                                <FormControl>
                                    <FormLabel>Поиск по сообщениям</FormLabel>
                                    <Input
                                        value={filters.searchText}
                                        onChange={(e) => handleFilterChange('searchText', e.target.value)}
                                        placeholder="Поиск в сообщениях..."
                                    />
                                </FormControl>

                                {/* Кнопки действий */}
                                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                    <Button
                                        variant="outlined"
                                        color="neutral"
                                        onClick={handleReset}
                                        sx={{ flex: 1 }}
                                    >
                                        Сбросить
                                    </Button>
                                    <Button
                                        variant="solid"
                                        onClick={handleApply}
                                        sx={{ flex: 1 }}
                                    >
                                        Применить
                                    </Button>
                                </Stack>
                            </Stack>
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}